import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button } from './button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './dialog';

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface Pending extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        returnFocus.current = document.activeElement as HTMLElement | null;
        setPending((prev) => {
          prev?.resolve(false);
          return { ...options, resolve };
        });
      }),
    []
  );

  const close = useCallback(
    (ok: boolean) => {
      setPending((prev) => {
        prev?.resolve(ok);
        return null;
      });
      returnFocus.current?.focus?.();
    },
    []
  );

  // Focus the safe option first so Enter never confirms a destructive action by accident.
  useEffect(() => {
    if (pending) cancelRef.current?.focus();
  }, [pending]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={!!pending} onOpenChange={(open) => !open && close(false)}>
        {pending && (
          <DialogContent aria-labelledby={titleId}>
            <DialogHeader>
              <DialogTitle id={titleId}>{pending.title}</DialogTitle>
              {pending.body && <DialogDescription>{pending.body}</DialogDescription>}
            </DialogHeader>
            <DialogFooter>
              <Button ref={cancelRef} variant="outline" onClick={() => close(false)}>
                {pending.cancelLabel ?? 'Cancel'}
              </Button>
              <Button variant={pending.destructive ? 'destructive' : 'default'} onClick={() => close(true)}>
                {pending.confirmLabel ?? 'Confirm'}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </ConfirmContext.Provider>
  );
}

/** Promise-based confirm: `if (!(await confirm({ title, body, confirmLabel, destructive }))) return;` */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
}
